/* eslint-disable camelcase */
import React, { useState, useRef, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import './RentalCarsForm.css';
import { Accordion, Col, Row, Form } from 'react-bootstrap';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { SingleCalendar, TravelerSelector } from '../../../../components';
import SignatureCanvas from 'react-signature-canvas';
import { ReactComponent as UploadIcon } from '../../../../assets/images/icons/solar_upload-line-duotone.svg';
import { useTranslation } from '../../../../contexts/TranslationContext';
import debounce from "lodash.debounce";
import axios from "axios";
import Cookies from "js-cookie";
import { axiosGet } from '../../../../utils/apis/api';

const RentalCarsForm = forwardRef(({ 
  onChange = () => {}, 
  showErrors = false, 
  initialData = null ,
  selectedDates,
  url
}, ref) => {
  const [previewImage, setPreviewImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation();
  const Lang = Cookies.get("preferred_language") || "en";
  const [errors, setErrors] = useState({});
  const [activeAccordionKeys, setActiveAccordionKeys] = useState(['0']);
  const inputRefs = useRef({});
  const sigPadRef = useRef();
  const [infants, setInfants] = useState(0);

  // Map fields to accordion sections
  const fieldToAccordionMap = {
    // Personal Information
    full_name: '0',
    date_of_birth: '0',
    gender: '0',
    nationality: '0',
    passport_number: '0',
    contact_number_phone: '0',
    email: '0',
    
    // Rental Details
    drop_off_destination:'1',
    pickup_destination:'1',
    pickup_location: '1',
    drop_off_location: '1',
    pickup_date_and_time: '1',
    drop_off_date_and_time: '1',
    car_type: '1',
    
    // Payment
    payment_method: '2',
    billing_address: '2',
    
    // Supporting Documents
    drivers_license_front: '4',
    passport_copy: '4',
    
    // Declaration
    agree_terms: '5',
    signature: '5'
  };

  const [form, setForm] = useState({
    /* Personal Information */
    full_name: "",
    date_of_birth: null,
    gender: "",
    nationality: "",
    nationality_name: "",
    passport_number: "",
    contact_number_phone: "",
    email: "",
    residential_address: "",
    
    /* Rental Details */
    pickup_date_and_time: null,
    drop_off_destination: null,
    drop_off_destination_name: "",
    pickup_destination:null,
    pickup_destination_name: "",
    pickup_location: "",
    drop_off_location: "",
    drop_off_date_and_time: null,
    car_type: "",
    car_rental_company: "",
    adults: 1,
    children: 0,
    ages: [],
    special_requests: "",
    
    /* Payment */
    payment_method: "",
    billing_address: "",
    invoice_required: false,
    
    /* Travel & Contact */
    contact_person_destination: "",
    flight_details: "",
    
    /* Files */
    files: {},
    
    /* Declaration */
    agree_terms: false,
    signature: ""
  });

  // Initialize form with initialData
  useEffect(() => {
    if (initialData) {
      setForm({
        ...initialData,
        date_of_birth: initialData.date_of_birth ? new Date(initialData.date_of_birth) : null,
        pickup_date_and_time: initialData.pickup_date_and_time ? new Date(initialData.pickup_date_and_time) : null,
        drop_off_date_and_time: initialData.drop_off_date_and_time ? new Date(initialData.drop_off_date_and_time) : null,
      });
    }
  }, [initialData]);

  // Restore signature from initialData
  useEffect(() => {
    if (initialData?.signature && sigPadRef.current) {
      sigPadRef.current.fromDataURL(initialData.signature);
    }
  }, [initialData]);

  const required = {
    full_name: t("FullNameIsRequired"),
    date_of_birth: t("DateOfBirthIsRequired"),
    gender: t("GenderIsRequired"),
    nationality: t("PassportNumberIsRequired"),
    passport_number: t("PassportNumberIsRequired"),
    contact_number_phone: t("PhoneNumberIsRequired"),
    email: t("EmailAddressIsRequired"),
    drop_off_destination: t("DestinationCountryRequired"),
    pickup_destination: t("DestinationCountryRequired"),
    pickup_location: t("PickUpLocationIsRequired"),
    drop_off_location: t("DropOffLocationIsRequired"),
    pickup_date_and_time: t("PickupDateIsRequired"),
    drop_off_date_and_time: t("DropOffDateIsRequired"),
    car_type: t("CarTypeIsRequired"),
    payment_method: t("PaymentMethodIsRequired"),
    billing_address: t("BillingAddressIsRequired"),
    agree_terms: t("YouMustAgreeToTheTerms"),
    signature: t("SignatureIsRequired"),
  };

  const requiredFiles = {
    drivers_license_front: t("DriversLicenseFrontIsRequired"),
    passport_copy: t("PassportCopyIsRequired")
  };

  const validate = () => {
    const newErrors = {};
    
    // Validate required fields
    Object.entries(required).forEach(([key, msg]) => {
      if (!form[key] || (typeof form[key] === "string" && !form[key].trim())) {
        newErrors[key] = msg;
      }
    });
    
    // Validate required files
    Object.entries(requiredFiles).forEach(([key, msg]) => {
      if (!form.files[key]) {
        newErrors[`file_${key}`] = msg;
      }
    });

     if (form.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(form.email)) {
          newErrors.email = t("EmailValidation");
        }
      }

      if (form.contact_number_phone) {
        const phoneRegex = /^\+?[0-9]{6,15}$/;
        if (!phoneRegex.test(form.contact_number_phone))
          newErrors.contact_number_phone = t("InvalidPhoneNumberFormat");
      }

      if (form.contact_person_destination) {
        const phoneRegex = /^\+?[0-9]{6,15}$/;
        if (!phoneRegex.test(form.contact_person_destination))
          newErrors.contact_person_destination = t("InvalidPhoneNumberFormat");
      }
    
    // Validate date ranges
    if (form.pickup_date_and_time && form.drop_off_date_and_time &&
        new Date(form.pickup_date_and_time) > new Date(form.drop_off_date_and_time)) {
      newErrors.drop_off_date_and_time = "Drop-off date must be after pickup date";
    }

          if (
        form.children > 0 &&
        (form.ages.length < form.children ||
          form.ages.includes(null) ||
          form.ages.includes(0))
      ) {
        newErrors.children = t("ValidationChildrenAge");
      }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Notify parent about form changes
  useEffect(() => {
    const isValid = validate();
    onChange(form, isValid);
  }, [form]);

  const [natQuery, setNatQuery] = useState("");
  const [natOptions, setNatOptions] = useState([]);
  const [natLoading, setNatLoading] = useState(false);

  // const fetchNationalities = useMemo(
  //   () =>
  //     debounce(async (q = "") => {
  //       try {
  //         setNatLoading(true);
  //         const params = q ? { search: q, limit: 20 } : { limit: 20 };
  //         const { data } = await axios.get(
  //           "https://crm.alltimestravels.com/api/method/alltimes.www.api.countries",
  //           { params }
  //         );
  //         setNatOptions(data.result.countries || []);
  //       } catch (e) {
  //         console.error("Error fetching nationalities:", e);
  //       } finally {
  //         setNatLoading(false);
  //       }
  //     }, 300),
  //   []
  // );

  const fetchNationalities = useMemo(
    () =>
      debounce(async (q = "") => {
        try {
          setNatLoading(true);
          const params = q ? { search: q, limit: 20 } : { limit: 20 };
          const data = await axiosGet(
            "https://crm.alltimestravels.com/api/method/alltimes.www.api.countries",
            params
          );
          setNatOptions(data?.result?.countries || []);
        } catch (e) {
          console.error(e);
        } finally {
          setNatLoading(false);
        }
      }, 300),
    []
  );

  useEffect(() => {
    return () => fetchNationalities.cancel();
  }, [fetchNationalities]);

  useEffect(() => {
    fetchNationalities(natQuery);
  }, [natQuery, fetchNationalities]);

  const [destQuery, setDestQuery] = useState("");
  const [destOptions, setDestOptions] = useState([]);
  const [destLoading, setDestLoading] = useState(false);

  // const fetchDestinations = useMemo(
  //   () =>
  //     debounce(async (q = "") => {
  //       try {
  //         setDestLoading(true);
  //         const baseParams = { location_type: "City", limit: 20 };
  //         const params =
  //           q.length >= 2 ? { ...baseParams, search: q } : baseParams;
  //         const { data } = await axios.get(
  //           "https://crm.alltimestravels.com/api/method/alltimes.www.api.locations",
  //           { params }
  //         );
  //         setDestOptions(data.result.locations || []);
  //       } catch (e) {
  //         console.error(e);
  //       } finally {
  //         setDestLoading(false);
  //       }
  //     }, 300),
  //   []
  // );


  const fetchDestinations = useMemo(
    () =>
      debounce(async (q = "") => {
        try {
          setDestLoading(true);
          const baseParams = { location_type: "City", limit: 20 };
          const params =
            q.length >= 2 ? { ...baseParams, search: q } : baseParams;
          const data = await axiosGet(
            "https://crm.alltimestravels.com/api/method/alltimes.www.api.locations",
            params
          );
          setDestOptions(data?.result?.locations || []);
        } catch (e) {
          console.error(e);
        } finally {
          setDestLoading(false);
        }
      }, 300),
    []
  );

  useEffect(() => {
    if (destQuery.length >= 2) fetchDestinations(destQuery);
  }, [destQuery, fetchDestinations]);

  useEffect(() => () => fetchDestinations.cancel(), [fetchDestinations]);

  const handleFileChange = (key, file) => {
    if (!file) return;
    setForm(prev => ({
      ...prev,
      files: { ...prev.files, [key]: file }
    }));
    if (inputRefs.current[key]) inputRefs.current[key].value = null;
  };

  const handleFileRemove = key => {
    setForm(prev => {
      const files = { ...prev.files };
      delete files[key];
      return { ...prev, files };
    });
    if (inputRefs.current[key]) inputRefs.current[key].value = null;
  };

      const truncate = (n, max = 28) =>
      !n ? "" : n.length <= max ? n : `${n.slice(0, max - 6)}...${n.slice(-5)}`;

    const fileDisplayName = (f) =>
      typeof f === "string" ? f.split("/").pop() : f.name;

  const  Filename = (name, maxLength = 30) => {
    if (!name || name.length <= maxLength) return name;
    const extIndex = name.lastIndexOf('.');
    const ext = extIndex !== -1 ? name.slice(extIndex) : '';
    const base = name.slice(0, maxLength - ext.length - 3);
    return `${base}...${ext}`;
  };

  const scrollToFirstError = () => {
    const firstErrorKey = Object.keys(errors)[0];
    if (firstErrorKey) {
      const accordionKey = fieldToAccordionMap[firstErrorKey];
      if (accordionKey && !activeAccordionKeys.includes(accordionKey)) {
        setActiveAccordionKeys(prev => [...prev, accordionKey]);
      }

      setTimeout(() => {
        const element = document.getElementById(`field-${firstErrorKey}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  };

  // Expose scroll method via ref
  useImperativeHandle(ref, () => ({
    scrollToFirstError
  }));

  // Auto-expand accordions when errors appear
  useEffect(() => {
    if (showErrors && Object.keys(errors).length > 0) {
      const errorAccordionKeys = Object.keys(errors)
        .map(field => fieldToAccordionMap[field])
        .filter(key => key !== undefined);
      
      setActiveAccordionKeys(prev => [
        ...prev,
        ...errorAccordionKeys.filter(key => !prev.includes(key))
      ]);
    }
  }, [showErrors, errors]);

  const clearSignature = () => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
      setForm(prev => ({ ...prev, signature: "" }));
    }
  };

  const carTypeOptions = [
    { name: 'Economy', label: t('Economy') },
    { name: 'Standard', label: t('Standard') },
    { name: 'Luxury', label: t('Luxury') },
    { name: 'SUV', label: t('SUV') },
    { name: 'Convertible', label: t('Convertible') },
  ];

  const paymentOptions = [
    { name: 'Credit Card', label: t('CreditCard') },
    { name: 'PayPal', label: t('PayPal') },
    { name: 'Bank Transfer', label: t('BankTransfer') },
  ];

  const renderAutocomplete = ({ options, value, setValue, placeholder = t('Select') }) => (
    <Autocomplete
      fullWidth
      options={options}
      getOptionLabel={o => o.label}
      value={value ? options.find(opt => opt.name === value) : null}
      onChange={(_, newVal) => setValue(newVal?.name || null)}
      isOptionEqualToValue={(opt, val) => opt.name === val?.name}
      renderInput={params => <TextField {...params} size="small" placeholder={placeholder} />}
      renderOption={(props, option) => <li {...props} key={option.name}>{option.label}</li>}
    />
  );

  const err = (field) => showErrors && errors[field];
  
  return (
    <Accordion 
      alwaysOpen 
      className="hotel-form-accordion services-rental-car-form-accordion-container"
      activeKey={activeAccordionKeys}
      onSelect={(keys) => setActiveAccordionKeys(keys)}
    >
      {/* 1. Personal Information */}
      <Accordion.Item eventKey="0" className="hotel-form-accordion-item">
        <Accordion.Header className="hotel-form-accordion-header">
          1. {t('PersonalInformation')}
        </Accordion.Header>
        <Accordion.Body className="hotel-form-accordion-body">
          <Row>
            {[
              { key: 'full_name', label: t('FullName'), placeholder: t('FullNameAsPerPassport') },
              { key: 'passport_number', label: t('PassportNumber'), placeholder: t('PassportNumber') },
              { key: 'contact_number_phone', label: t('ContactNumber'), placeholder: t('PhoneNumber') },
              { key: 'email', label: t('Email'), placeholder: t('EmailAddress') },
              { key: 'residential_address', label: t('ResidentialAddress'), placeholder: t('ResidentialAddress') },
            ].map(({ key, label, placeholder }) => (
              <Col xl={4} md={6} key={key}>
                <div 
                  className="hotel-form-input-container" 
                  id={`field-${key}`}
                >
                  <label className="hotel-form-input-label">
                    {label}
                    {required[key] && <span>*</span>}
                  </label>
                  <input
                    type="text"
                    className="hotel-form-input"
                    placeholder={placeholder}
                    value={form[key] || ''}
                    onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  />
                  {err(key) && <div className="field-error">{errors[key]}</div>}
                </div>
              </Col>
            ))}

            <Col xl={4} md={6}>
              <div 
                className="hotel-form-input-container" 
                id="field-nationality"
              >
                <label className="hotel-form-input-label">
                  {t('Nationality')}
                  <span>*</span>
                </label>
                <Autocomplete
                  options={natOptions}
                  loading={natLoading}
                  getOptionLabel={(o) => o.name || ""}
                  // value={natOptions.find(o => o.name === form.nationality) || null}
                  value={form.nationality ? {
                    name: form.nationality,
                    country_name: form.nationality_name || "",
                  } : null}
                  onInputChange={(_, v) => setNatQuery(v)}
                  onChange={(_, v) => 
                    setForm(p => ({ ...p, nationality: v?.name || null }))
                  }
                  sx={{
                      ".MuiAutocomplete-inputRoot": {
                        fontSize: "12px",
                        flexDirection: Lang === "ar" ? "row-reverse" : "row",
                      },
                      ".MuiAutocomplete-endAdornment": {
                        left:
                          Lang === "ar" ? "9 !important" : "unset !important",
                        right:
                          Lang === "ar" ? "unset !important" : "9 !important",
                      },
                      ".MuiInputBase-input": {
                        textAlign: Lang === "ar" ? "right" : "left",
                      },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={t('SelectNationality')}
                      InputProps={{
                          ...params.InputProps,
                          style: {
                            direction: Lang === "ar" ? "rtl" : "ltr",
                          },
                          sx: {
                            backgroundColor: "#FFF",
                            borderRadius: "6px",
                            padding: "9px 15px !important",
                            fontFamily: '"Dmsans400"',
                            fontSize: "14px !important",
                            fontWeight: 400,
                            letterSpacing: "0.25px",
                          },
                      }}
                    />
                  )}
                />
                {err('nationality') && <div className="field-error">{errors.nationality}</div>}
              </div>
            </Col>

            <Col xl={4} md={6}>
              <div 
                className="hotel-form-input-container" 
                id="field-date_of_birth"
              >
                <label className="hotel-form-input-label">
                  {t('DateOfBirth')}
                  <span>*</span>
                </label>
                <SingleCalendar
                  value={form.date_of_birth}
                  onChange={e => setForm(p => ({ ...p, date_of_birth: e.value }))}
                  placeholder={t('DateOfBirth')}
                  showTime={false}
                />
                {err('date_of_birth') && <div className="field-error">{errors.date_of_birth}</div>}
              </div>
            </Col>

            <Col xl={4} md={4}>
              <div 
                className="hotel-form-input-container" 
                id="field-gender"
              >
                <label className="hotel-form-input-label">
                  {t('Gender')}
                  <span>*</span>
                </label>
                <Form.Select
                  size="sm"
                  value={form.gender}
                  onChange={e => setForm(p => ({ ...p, gender: e.target.value }))}
                  className="hotel-form-input select-dropdown"
                >
                  <option value="" disabled hidden>{t('SelectYourGender')}</option>
                  <option value="male">{t('Male')}</option>
                  <option value="female">{t('Female')}</option>
                </Form.Select>
                {err('gender') && <div className="field-error">{errors.gender}</div>}
              </div>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>

      {/* 2. Rental Car Booking Details */}
      <Accordion.Item eventKey="1" className="hotel-form-accordion-item">
        <Accordion.Header className="hotel-form-accordion-header">
          2. {t('RentalCarBookingDetails')}
        </Accordion.Header>
        <Accordion.Body className="hotel-form-accordion-body">
          <Row>
            {/*  Drop Off Destination */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-destination">
                <label className="hotel-form-input-label">
                   {t("DropOffDestination")} <span>*</span>
                </label>
                <Autocomplete
                  options={destOptions}
                  loading={destLoading}
                  getOptionLabel={(o) => {
                    const city = (o.location_name || "").replace(/\s+/g, " ").trim();
                    const parent = (o.parent_location_name || "").replace(/\s+/g, " ").trim();
                    return parent ? `${city} (${parent})` : city;
                  }}
                  value={form.drop_off_destination ? {
                    name: form.drop_off_destination,
                    location_name: form.drop_off_destination_name || "",
                  } : null}
                  onOpen={() => !destOptions.length && fetchDestinations()}
                  onInputChange={(_, v, r) => r === "input" && setDestQuery(v)}
                  onChange={(_, v) => setForm(p => ({
                    ...p,
                    drop_off_destination: v?.name || null,
                    drop_off_destination_name: v?.location_name || "",
                  }))}
                  sx={{
                      ".MuiAutocomplete-inputRoot": {
                        fontSize: "12px",
                        flexDirection: Lang === "ar" ? "row-reverse" : "row",
                      },
                      ".MuiAutocomplete-endAdornment": {
                        left:
                          Lang === "ar" ? "9 !important" : "unset !important",
                        right:
                          Lang === "ar" ? "unset !important" : "9 !important",
                      },
                      ".MuiInputBase-input": {
                        textAlign: Lang === "ar" ? "right" : "left",
                      },
                  }}
                  renderInput={(p) => (
                    <TextField
                      {...p}
                      size="small"
                      placeholder={t("SelectCountryOrCity")}
                      InputProps={{
                          ...p.InputProps,
                          style: {
                            direction: Lang === "ar" ? "rtl" : "ltr",
                          },
                          sx: {
                            backgroundColor: "#FFF",
                            borderRadius: "6px",
                            padding: "9px 15px !important",
                            fontFamily: '"Dmsans400"',
                            fontSize: "14px !important",
                            fontWeight: 400,
                            letterSpacing: "0.25px",
                          },
                    }}
                    />
                  )}
                />
                {err("drop_off_destination") && <div className="field-error">{errors.drop_off_destination}</div>}
              </div>
            </Col>

            {/*  pick up  Destination */}
            <Col xl={4} md={6}>
              <div className="hotel-form-input-container" id="field-destination">
                <label className="hotel-form-input-label">
                   {t("PickupDestination")} <span>*</span>
                </label>
                <Autocomplete
                  options={destOptions}
                  loading={destLoading}
                  getOptionLabel={(o) => {
                    const city = (o.location_name || "").replace(/\s+/g, " ").trim();
                    const parent = (o.parent_location_name || "").replace(/\s+/g, " ").trim();
                    return parent ? `${city} (${parent})` : city;
                  }}
                  value={form.pickup_destination ? {
                    name: form.pickup_destination,
                    location_name: form.pickup_destination_name || "",
                  } : null}
                  onOpen={() => !destOptions.length && fetchDestinations()}
                  onInputChange={(_, v, r) => r === "input" && setDestQuery(v)}
                  onChange={(_, v) => setForm(p => ({
                    ...p,
                    pickup_destination: v?.name || null,
                    pickup_destination_name: v?.location_name || "",
                  }))}
                  sx={{
                      ".MuiAutocomplete-inputRoot": {
                        fontSize: "12px",
                        flexDirection: Lang === "ar" ? "row-reverse" : "row",
                      },
                      ".MuiAutocomplete-endAdornment": {
                        left:
                          Lang === "ar" ? "9 !important" : "unset !important",
                        right:
                          Lang === "ar" ? "unset !important" : "9 !important",
                      },
                      ".MuiInputBase-input": {
                        textAlign: Lang === "ar" ? "right" : "left",
                      },
                  }}
                  renderInput={(p) => (
                    <TextField
                      {...p}
                      size="small"
                      placeholder={t("SelectCountryOrCity")}
                      InputProps={{
                          ...p.InputProps,
                          style: {
                            direction: Lang === "ar" ? "rtl" : "ltr",
                          },
                          sx: {
                            backgroundColor: "#FFF",
                            borderRadius: "6px",
                            padding: "9px 15px !important",
                            fontFamily: '"Dmsans400"',
                            fontSize: "14px !important",
                            fontWeight: 400,
                            letterSpacing: "0.25px",
                          },
                    }}
                    />
                  )}
                />
                {err("pickup_destination") && <div className="field-error">{errors.pickup_destination}</div>}
              </div>
            </Col>

            {[
              { 
                key: 'pickup_location', 
                label: t('pickupLocation'),
                placeholder: t('CityAirportOrRentalOffice')
              },
              { 
                key: 'drop_off_location', 
                label: t('dropOffLocation'),
                placeholder: t('CityAirportOrRentalOffice')
              },
            ].map(({ key, label, placeholder }) => (
              <Col xl={6} md={6} key={key}>
                <div 
                  className="hotel-form-input-container"
                  id={`field-${key}`}
                >
                  <label className="hotel-form-input-label">
                    {label}
                    <span>*</span>
                  </label>
                  <input
                    type="text"
                    className="hotel-form-input"
                    placeholder={placeholder}
                    value={form[key] || ''}
                    onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  />
                  {err(key) && <div className="field-error">{errors[key]}</div>}
                </div>
              </Col>
            ))}
  
            <Col xl={6} md={6}>
              <div 
                className="hotel-form-input-container"
                id="field-pickup_date_and_time"
              >
                <label className="hotel-form-input-label">
                  {t('pickupDate')}
                  <span>*</span>
                </label>
                <SingleCalendar
                  value={form.pickup_date_and_time}
                  onChange={e => setForm(p => ({ ...p, pickup_date_and_time: e.value }))}
                  placeholder={t('pickupDate')}
                  min={new Date(selectedDates.startDate)}
                  max={
                      form.drop_off_date_and_time
                        ? new Date(
                            Math.min(
                              new Date(form.drop_off_date_and_time).getTime(),
                              new Date(selectedDates.endDate).getTime()
                            )
                          )
                        : new Date(selectedDates.endDate)
                  }
                  viewDate={new Date(selectedDates.startDate)}
                  showTime={true}
                />
                {err('pickup_date_and_time') && <div className="field-error">{errors.pickup_date_and_time}</div>}
              </div>
            </Col>
            
            <Col xl={6} md={6}>
              <div 
                className="hotel-form-input-container"
                id="field-drop_off_date_and_time"
              >
                <label className="hotel-form-input-label">
                  {t('DropOffDate')}
                  <span>*</span>
                </label>
                <SingleCalendar
                  value={form.drop_off_date_and_time}
                  onChange={e => setForm(p => ({ ...p, drop_off_date_and_time: e.value }))}
                  placeholder={t('DropOffDate')}
                  min={
                      form.pickup_date_and_time
                        ? new Date(
                            Math.max(
                              new Date(form.pickup_date_and_time).getTime(),
                              new Date(selectedDates.startDate).getTime()
                            )
                          )
                        : new Date(selectedDates.startDate)
                  }
                  max={new Date(selectedDates.endDate)}
                  viewDate={new Date(selectedDates.endDate)}
                  showTime={true}
                />
                {err('drop_off_date_and_time') && <div className="field-error">{errors.drop_off_date_and_time}</div>}
              </div>
            </Col>

            <Col xl={4} md={6}>
              <div 
                className="hotel-form-input-container"
                id="field-car_type"
              >
                <label className="hotel-form-input-label">
                  {t('CarType')}
                  <span>*</span>
                </label>
                {renderAutocomplete({
                  options: carTypeOptions,
                  value: form.car_type,
                  setValue: val => setForm(p => ({ ...p, car_type: val }))
                })}
                {err('car_type') && <div className="field-error">{errors.car_type}</div>}
              </div>
            </Col>

            <Col xl={4} md={6}>
              <div className="hotel-form-input-container">
                <label className="hotel-form-input-label">
                  {t('CarRentalCompany')}
                </label>
                <input
                  type="text"
                  className="hotel-form-input"
                  placeholder={t('CarRentalCompany')}
                  value={form.car_rental_company || ''}
                  onChange={e => setForm(p => ({ ...p, car_rental_company: e.target.value }))}
                />
              </div>
            </Col>

            <Col xl={4} md={6}>
              <div className="hotel-form-input-container">
                <label className="hotel-form-input-label">
                  {t('NumberOfTravelers')} 
                  <span>*</span>
                </label>
                <TravelerSelector
                  adults={form.adults}
                  children={form.children}
                  childrenAges={form.ages}
                  infants={infants}
                  onAdultsChange={(val) => setForm(p => ({ 
                    ...p, 
                    adults: parseInt(val) || 1 
                  }))}
                  onChildrenChange={(val) => {
                    const count = parseInt(val) || 0;
                    setForm(p => {
                      const newAges = [...p.ages];
                      while (newAges.length < count) newAges.push("");
                      while (newAges.length > count) newAges.pop();
                      return { 
                        ...p, 
                        children: count,
                        ages: newAges
                      };
                    });
                  }}
                  onChildrenAgesChange={(newAges) => setForm(p => ({ ...p, ages: newAges }))}
                  onInfantsChange={setInfants}
                />
                {err("children") && (
                    <div className="field-error">{errors.children}</div>
                )}
              </div>
            </Col>

            <Col xl={12}>
              <div className="hotel-form-input-container">
                <label className="hotel-form-input-label">
                  {t('specialRequest')}
                </label>
                <textarea
                  rows="3"
                  className="hotel-form-input"
                  placeholder={t('EnterAnySpecialRequests')}
                  value={form.special_requests}
                  onChange={e => setForm(p => ({ ...p, special_requests: e.target.value }))}
                />
              </div>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>

      {/* 3. Payment & Billing Information */}
      <Accordion.Item eventKey="2" className="hotel-form-accordion-item">
        <Accordion.Header className="hotel-form-accordion-header">
          3. {t('PaymentAndBillingInformation')}
        </Accordion.Header>
        <Accordion.Body className="hotel-form-accordion-body">
          <Row>
            <Col xl={4} md={6}>
              <div 
                className="hotel-form-input-container"
                id="field-payment_method"
              >
                <label className="hotel-form-input-label">
                  {t('PaymentMethod')}
                  <span>*</span>
                </label>
                {renderAutocomplete({
                  options: paymentOptions,
                  value: form.payment_method,
                  setValue: val => setForm(p => ({ ...p, payment_method: val }))
                })}
                {err('payment_method') && <div className="field-error">{errors.payment_method}</div>}
              </div>
            </Col>

            <Col xl={8} md={6}>
              <div 
                className="hotel-form-input-container"
                id="field-billing_address"
              >
                <label className="hotel-form-input-label">
                  {t('BillingAddress')}
                  <span>*</span>
                </label>
                <input
                  type="text"
                  className="hotel-form-input"
                  placeholder={t('BillingAddress')}
                  value={form.billing_address}
                  onChange={e => setForm(p => ({ ...p, billing_address: e.target.value }))}
                />
                {err('billing_address') && <div className="field-error">{errors.billing_address}</div>}
              </div>
            </Col>

            <Col xl={12}>
              <div className="add-service-form-checkbox-container">
                <div className="add-service-form-checkbox-title">
                  {t('InvoiceRequired')}
                </div>
                <Form className="add-service-form-checkboxes">
                  {[{value: true, label: t('Yes')}, {value: false, label: t('No')}].map(opt => (
                    <Form.Check
                      key={opt.label}
                      inline
                      type="radio"
                      label={opt.label}
                      name="invoiceRequired"
                      checked={form.invoice_required === opt.value}
                      onChange={() => setForm(p => ({ ...p, invoice_required: opt.value }))}
                      className="add-service-form-checkbox"
                    />
                  ))}
                </Form>
              </div>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>

      {/* 4. Travel & Contact Details */}
      <Accordion.Item eventKey="3" className="hotel-form-accordion-item">
        <Accordion.Header className="hotel-form-accordion-header">
          4. {t('TravelContactDetails')}
        </Accordion.Header>
        <Accordion.Body className="hotel-form-accordion-body">
          <Row>
            {[
              {
                key: 'flight_details',
                label: t('FlightDetails'),
                placeholder: t('FlightDetails')
              },
              {
                key: 'contact_person_destination',
                label: t('ContactPersonDestination'),
                placeholder: t('ContactPersonDestination')
              },
            ].map(({ key, label, placeholder }) => (
              <Col xl={6} md={6} key={key}>
                <div className="hotel-form-input-container">
                  <label className="hotel-form-input-label">{label}</label>
                  <input
                    type="text"
                    className="hotel-form-input"
                    placeholder={placeholder}
                    value={form[key] || ''}
                    onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  />
                  {key === "contact_person_destination" &&
                      err("contact_person_destination") && (
                        <div className="field-error">
                          {errors.contact_person_destination}
                        </div>
                  )}
                </div>
              </Col>
            ))}
          </Row>
        </Accordion.Body>
      </Accordion.Item>

      {/* 5. Supporting Documents */}
      <Accordion.Item eventKey="4" className="hotel-form-accordion-item">
        <Accordion.Header className="hotel-form-accordion-header">
          5. {t('SupportingDocuments')}
        </Accordion.Header>
        <Accordion.Body className="hotel-form-accordion-body">
          <Row>
            {[
              { 
                key: 'drivers_license_front', 
                label: t('DriversLicenseFrontAndBack'), 
                fieldId: 'file_drivers_license_front'
              },
              { 
                key: 'passport_copy', 
                label: t('PassportCopy'), 
                fieldId: 'file_passport_copy'
              },
              { 
                key: 'credit_card', 
                label: t('CreditCardSecurity')
              },
              { 
                key: 'travel_insurance', 
                label: t('TravelInsuranceIfRequired')
              },
              { 
                key: 'special_travel_permits', 
                label: t('SpecialTravelPermitsIifApplicable')
              },
            ].map(({ key, label, fieldId }) => {
              const file = form.files[key];
              const isRequired = requiredFiles[key];
                const isPdf =
                  file?.name?.toLowerCase().endsWith(".pdf") ||
                  (typeof file === "string" &&
                    file.toLowerCase().endsWith(".pdf"));
              
              return (
                <Col xl={6} md={6} key={key}>
                  <div
                    className="hotel-form-input-container"
                    id={fieldId || `field-file_${key}`}
                  >
                    <label className="hotel-form-input-label">
                      {label}
                      {isRequired && <span>*</span>}
                    </label>
                    <div className="custom-upload-wrapper">
                      <input
                        id={`file-${key}`}
                        type="file"
                        accept=".pdf, .png, .jpeg, .jpg"
                        style={{ display: "none" }}
                        ref={(el) => {
                          if (el) inputRefs.current[key] = el;
                        }}
                        onChange={(e) =>
                          handleFileChange(key, e.target.files[0])
                        }
                      />
                      {/* <label htmlFor={`file-${key}`} className="custom-upload-box">
                        {!file ? (
                          <>
                            <span className="upload-placeholder">
                              {t('SupportedFormats')}: JPEG, PNG, PDF
                            </span>
                            <UploadIcon className="upload-svg-icon" />
                          </>
                        ) : (
                          <>
                            <span className="upload-filename">
                              {truncateFilename(typeof file === 'string' 
                                ? file.split('/').pop() 
                                : file.name)}
                            </span>
                            <span
                              className="upload-remove"
                              onClick={e => {
                                e.preventDefault();
                                handleFileRemove(key);
                              }}
                            >
                              ×
                            </span>
                          </>
                        )}
                      </label> */}

                      {file ? (
                        <div className="custom-upload-box">
                          <div className="file-info">
                            {isPdf ? (
                              <a
                                href={
                                  typeof file === "string"
                                    ? `${url}${file}`
                                    : URL.createObjectURL(file)
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="file-link"
                              >
                                {truncate(fileDisplayName(file))}
                                <span className="file-type-badge">PDF</span>
                              </a>
                            ) : (
                              <span className="file-name">
                                {truncate(fileDisplayName(file))}
                              </span>
                            )}
                          </div>

                          <div className="file-actions">
                            <button
                              type="button"
                              className="file-action-btn view-file-btn"
                              onClick={() => {
                                if (isPdf) {
                                  window.open(
                                    typeof file === "string"
                                      ? `${url}${file}`
                                      : URL.createObjectURL(file),
                                    "_blank"
                                  );
                                } else {
                                  const imageUrl =
                                    typeof file === "string"
                                      ? `${url}${file}`
                                      : URL.createObjectURL(file);
                                  setPreviewImage(imageUrl);
                                  setShowModal(true);
                                }
                              }}
                            >
                              {isPdf ? t("View") : t("Preview")}
                            </button>

                            <button
                              type="button"
                              className="file-action-btn remove-file-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                handleFileRemove(key);
                              }}
                            >
                              {t("Remove")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label
                          htmlFor={`file-${key}`}
                          className="custom-upload-box"
                        >
                          <span className="upload-placeholder">
                            {t("Supported")}: {t("Jpeg")}, {t("Png")},{" "}
                            {t("Pdf")}
                          </span>
                          <UploadIcon className="upload-svg-icon" />
                        </label>
                      )}
                    </div>
                    {isRequired && err(`file_${key}`) && (
                      <div className="field-error">{errors[`file_${key}`]}</div>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        </Accordion.Body>
      </Accordion.Item>

      {/* 6. Declaration & Signature */}
      <Accordion.Item eventKey="5" className="hotel-form-accordion-item">
        <Accordion.Header className="hotel-form-accordion-header">
          6. {t('DeclarationAndSignature')}
        </Accordion.Header>
        <Accordion.Body className="hotel-form-accordion-body">
          <Row>
            <Col xl={12}>
              <div 
                className="hotel-form-input-container"
                id="field-agree_terms"
              >
                <Form.Check
                  type="checkbox"
                  label={t('IAgreeToTheTermsAndConditions')}
                  checked={form.agree_terms}
                  onChange={e => setForm(p => ({ ...p, agree_terms: e.target.checked }))}
                  className="hotel-form-input-label"
                />
                {err('agree_terms') && <div className="field-error">{errors.agree_terms}</div>}
              </div>
            </Col>

            <Col xl={6}>
              <div 
                className="hotel-form-input-container"
                id="field-signature"
              >
                <label className="hotel-form-input-label">
                  {t('Signature')}
                  <span>*</span>
                </label>
                <div className="signature-container" style={{ position: "relative" }}>
                  <SignatureCanvas
                    ref={sigPadRef}
                    penColor="black"
                    canvasProps={{
                      width: 500,
                      height: 150,
                      className: "sigCanvas",
                    }}
                    onEnd={() => setForm(p => ({ 
                      ...p, 
                      signature: sigPadRef.current.toDataURL() 
                    }))}
                  />
                  <button
                    onClick={clearSignature}
                    title="Clear"
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 40,
                      background: "none",
                      border: "none",
                    }}
                  >
                    ⟳
                  </button>
                </div>
                {err('signature') && <div className="field-error">{errors.signature}</div>}
              </div>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>
              {previewImage && (
          <div
            className={`modal fade ${
              showModal ? "show d-block" : ""
            } service-files-modal`}
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            onClick={() => setShowModal(false)}
          >
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowModal(false)}
                ></button>
                <div className="service-image-file">
                  <img
                    // src={initialData ? `${url}${previewImage}` : previewImage}
                    src={previewImage}
                    alt="preview"
                    style={{ maxWidth: "100%" }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
    </Accordion>
  );
});

export default RentalCarsForm;